/**
 * Product gallery video autoplay.
 *
 * Makes every video of a product media gallery (<media-gallery>) show up playing,
 * all of them at the same time, no matter how many the product has. Images are
 * left alone, and the gallery falls back to its regular click-to-play behaviour
 * when "Autoplay gallery videos" is switched off in the section settings.
 *
 * Why the markup alone is not enough:
 * - iOS Safari (and Chrome on Android) only start a video unattended when it is
 *   muted and inline. sections/main-product.liquid renders it that way, and
 *   prepareVideo() re-asserts it, because those properties are easy to lose on the
 *   way through <template> / cloneNode().
 * - The video lives inside <deferred-media><template>, so it is not in the DOM at
 *   all until it is cloned out. jumpstart() does the cloning without the
 *   window.pauseAllMedia() call that DeferredMedia.loadContent() makes, since that
 *   would stop the gallery videos we just started.
 * - Safari refuses play() while the tab is in the background, and it pauses
 *   autoplaying videos that leave the viewport, so playback is (re)started when a
 *   video comes back into view and whenever the page becomes visible again.
 * - If play() is refused twice anyway (Low Power Mode, data saver), the poster is
 *   handed back as a play button and playback is retried from the first real user
 *   interaction. A real gesture is used instead of a synthetic element.click(),
 *   because a synthetic click carries no user activation and Safari ignores it.
 */
(function () {
  'use strict';

  var GALLERY_SELECTOR = 'media-gallery';
  var MEDIA_SELECTOR = '.product__media-list [data-media-id]';
  var LOADED_ATTRIBUTE = 'data-gallery-videos';
  var POSTER_SELECTOR = '.deferred-media__poster';
  var GESTURE_EVENTS = ['pointerdown', 'touchend', 'keydown'];

  /** @type {WeakMap<Element, {tries: number, blocked: boolean, visible: boolean}>} */
  var state = new WeakMap();
  /** @type {Array<Element>} */
  var retryQueue = [];
  var gestureListenersBound = false;
  var observer = null;

  /* ------------------------------------------------------- element helpers -- */

  function deferredOf(item) {
    return item.querySelector('.deferred-media');
  }

  function insideOf(deferredMedia) {
    return deferredMedia.querySelector('video, iframe, model-viewer');
  }

  function entryOf(deferredMedia) {
    var current = state.get(deferredMedia);
    if (!current) {
      current = { tries: 0, blocked: false, visible: true };
      state.set(deferredMedia, current);
    }
    return current;
  }

  function isEmbed(media) {
    return media.nodeName === 'IFRAME' && (media.classList.contains('js-youtube') || media.classList.contains('js-vimeo'));
  }

  function isVideo(media) {
    return media.nodeName === 'VIDEO';
  }

  function showPoster(deferredMedia) {
    deferredMedia.removeAttribute('loaded');
    var poster = deferredMedia.querySelector(POSTER_SELECTOR);
    if (poster) poster.setAttribute('role', 'button');
  }

  function hidePoster(deferredMedia) {
    var poster = deferredMedia.querySelector(POSTER_SELECTOR);
    if (poster) poster.removeAttribute('role');
  }

  /**
   * The video is appended after the poster, so it is taken out of the flow and
   * pinned over the frame the poster image already gives the media item. Keeping
   * this inline (rather than in CSS) is what lets the script hand the same element
   * back and forth between "playing" and "poster is the play button again".
   */
  function frameVideo(video) {
    video.style.position = 'absolute';
    video.style.inset = '0';
    video.style.width = '100%';
    video.style.height = '100%';
    video.style.objectFit = 'contain';
    video.style.borderRadius = 'calc(var(--media-radius) - var(--media-border-width))';

    if (!video.style.display || video.style.display === 'none') {
      video.style.display = 'block';
    }
  }

  /** Takes the video out of the way while the poster stands in for it. */
  function setVideoHidden(video, hidden) {
    if (hidden) {
      video.style.display = 'none';
    } else {
      frameVideo(video);
    }
  }

  /** Poster shown again after a refusal: clicking it is the one gesture we are sure about. */
  function onPosterClick(deferredMedia, media) {
    if (!entryOf(deferredMedia).blocked) return;
    retryFromGesture([{ deferredMedia: deferredMedia, media: media }]);
  }

  /**
   * "playing" is the single source of truth for a video that really is running:
   * it clears an earlier refusal and puts the poster away for good. It also
   * protects against the theme's own pauseAllMedia() landing between our play()
   * call and the first frame, which would otherwise leave the gallery on posters.
   */
  function onPlaybackStarted(deferredMedia, media) {
    var current = entryOf(deferredMedia);
    current.tries = 0;
    current.blocked = false;
    setVideoHidden(media, false);
    hidePoster(deferredMedia);
    deferredMedia.setAttribute('loaded', '');
  }

  function wireDeferredMedia(deferredMedia, media) {
    if (!isVideo(media)) return;

    media.addEventListener('playing', function () {
      onPlaybackStarted(deferredMedia, media);
    });
    media.addEventListener('pause', function () {
      var current = entryOf(deferredMedia);
      if (current.blocked || media.ended) return;
      if (!current.visible) return; // off screen on purpose
      // Paused by something else on the page (highlighted media, modal, cart
      // drawer). Ask to carry on; a refusal here is not held against the video.
      resume(media);
    });

    // The poster is the play button of the fallback: a click here is the cleanest
    // possible gesture and must not be swallowed by DeferredMedia's click handler.
    var poster = deferredMedia.querySelector(POSTER_SELECTOR);
    if (poster && poster.dataset.galleryVideosWired !== 'true') {
      poster.dataset.galleryVideosWired = 'true';
      poster.addEventListener('click', function () {
        onPosterClick(deferredMedia, media);
      });
    }
  }

  /* ---------------------------------------------------------------- markup -- */

  /**
   * Clones the deferred media element into the gallery and returns it.
   * Mirrors DeferredMedia.loadContent() minus window.pauseAllMedia().
   */
  function jumpstart(deferredMedia) {
    var template = deferredMedia.querySelector('template');
    if (!template || !template.content) return null;

    var source = template.content.querySelector('video, iframe, model-viewer');
    if (!source) return null;

    var media = source.cloneNode(true);
    deferredMedia.setAttribute('loaded', true);
    deferredMedia.appendChild(media);
    wireDeferredMedia(deferredMedia, media);

    if (isEmbed(media)) {
      // Same "workaround for safari iframe bug" the theme applies in loadContent().
      var formerStyle = media.getAttribute('style');
      media.setAttribute('style', 'display: block;');
      window.setTimeout(function () {
        if (formerStyle === null) {
          media.removeAttribute('style');
        } else {
          media.setAttribute('style', formerStyle);
        }
      }, 0);
    }

    return media;
  }

  /** Makes a video element autoplay-friendly. Safe to call more than once. */
  function prepareVideo(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('autoplay', '');
    if (!video.getAttribute('preload')) video.setAttribute('preload', 'auto');
    video.classList.add('product-gallery-video');
    frameVideo(video);
  }

  /**
   * External embeds autoplay from their own URL parameters (see
   * snippets/product-thumbnail.liquid); re-asserting them here covers embeds that
   * were rendered before this script ran or added afterwards.
   */
  function prepareExternal(media) {
    var src = media.getAttribute('src') || '';
    if (!src) return;

    var needed = media.classList.contains('js-youtube')
      ? 'autoplay=1&mute=1&playsinline=1'
      : 'autoplay=1&muted=1&playsinline=1';

    if (src.indexOf('autoplay=1') === -1) {
      media.setAttribute('src', src + (src.indexOf('?') === -1 ? '?' : '&') + needed);
    }

    media.setAttribute('allow', 'autoplay; encrypted-media; fullscreen; picture-in-picture');
  }

  /* -------------------------------------------------------------- playback -- */

  /** Restarts playback after a browser-forced pause; never counts as a failure. */
  function resume(media) {
    if (!isVideo(media) || media.ended) return;
    if (!media.paused) return;

    var attempt = media.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
  }

  function playNow(deferredMedia, media) {
    var current = entryOf(deferredMedia);

    if (current.blocked) {
      // Waiting for a user gesture: keep the poster as the play button.
      showPoster(deferredMedia);
      return;
    }

    current.tries += 1;
    setVideoHidden(media, false);
    hidePoster(deferredMedia);

    var attempt = media.play();

    if (!attempt || typeof attempt.catch !== 'function') return;

    attempt.catch(function () {
      if (current.tries === 1) {
        // Safari refuses play() while the tab is hidden; retry once when it returns.
        document.addEventListener(
          'visibilitychange',
          function () {
            if (!document.hidden) playNow(deferredMedia, media);
          },
          { once: true }
        );
        return;
      }

      // Refused twice: stop fighting the policy. Poster back as a play button,
      // playback retried from the next real user gesture.
      current.blocked = true;
      media.removeAttribute('autoplay');
      media.pause();
      setVideoHidden(media, true);
      showPoster(deferredMedia);
      requestGestureRetry(deferredMedia, media);
    });
  }

  function play(media) {
    if (!media) return;

    var deferredMedia = media.closest('deferred-media');
    if (!deferredMedia) return;

    if (isVideo(media)) {
      prepareVideo(media);
      playNow(deferredMedia, media);
      return;
    }

    if (isEmbed(media)) prepareExternal(media);
  }

  /* ------------------------------------------------------ gesture fallback -- */

  function requestGestureRetry(deferredMedia, media) {
    var known = retryQueue.some(function (item) {
      return item.deferredMedia === deferredMedia;
    });
    if (known) return;

    retryQueue.push({ deferredMedia: deferredMedia, media: media });
    bindGestureListeners();
  }

  function retryFromGesture(items) {
    var queue = items || retryQueue;
    if (!items) retryQueue = [];

    queue.forEach(function (item) {
      if (!item.deferredMedia.isConnected || !item.media.isConnected) return;

      var current = entryOf(item.deferredMedia);
      current.blocked = false;
      current.tries = 0;

      playNow(item.deferredMedia, item.media);
    });
  }

  function bindGestureListeners() {
    if (gestureListenersBound || !retryQueue.length) return;
    gestureListenersBound = true;

    // pointerdown / touchend / keydown all grant transient user activation, which
    // is what unlocks playback. A synthetic element.click() would not.
    GESTURE_EVENTS.forEach(function (type) {
      document.addEventListener(type, retryFromGesture);
    });
  }

  /* ------------------------------------------------------------- observer -- */

  function createObserver() {
    return new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var media = entry.target;
          var deferredMedia = media.closest('deferred-media');
          if (!deferredMedia) return;

          var current = entryOf(deferredMedia);
          current.visible = entry.isIntersecting;

          if (!entry.isIntersecting) {
            // Off screen: an iOS video that keeps decoding while scrolled away can
            // be killed by the browser and then sit on a black frame forever.
            if (isVideo(media) && !media.paused) media.pause();
            return;
          }

          // Already running: nothing to do, and no second play() call either.
          if (isVideo(media) && !media.paused && !media.ended) return;

          play(media);
        });
      },
      { threshold: 0.1, rootMargin: '100px 0px' }
    );
  }

  /* -------------------------------------------------------------- gallery -- */

  function startGallery(gallery) {
    Array.prototype.forEach.call(gallery.querySelectorAll(MEDIA_SELECTOR), function (item) {
      if (item.hasAttribute(LOADED_ATTRIBUTE)) return;
      item.setAttribute(LOADED_ATTRIBUTE, 'done');

      var deferredMedia = deferredOf(item);
      if (!deferredMedia) return;

      var media = deferredMedia.hasAttribute('loaded') ? insideOf(deferredMedia) : jumpstart(deferredMedia);
      if (!media) return;

      if (deferredMedia.hasAttribute('loaded') && isVideo(media)) wireDeferredMedia(deferredMedia, media);

      if (isVideo(media) || isEmbed(media)) {
        if (observer) {
          observer.observe(media);
        } else {
          play(media);
        }
      }
    });
  }

  /**
   * The stock gallery pauses every video of the page whenever the active media
   * changes (playActiveMedia). With the whole gallery playing at once that would
   * silence the other videos, so the pause is dropped for this gallery only.
   */
  function keepAllVideosPlaying() {
    if (!customElements.get('media-gallery')) return;

    var prototype = customElements.get('media-gallery').prototype;
    if (prototype.productGalleryVideosPatched) return;
    prototype.productGalleryVideosPatched = true;

    prototype.playActiveMedia = function (activeItem) {
      var deferredMedia = activeItem && activeItem.querySelector('.deferred-media');
      if (deferredMedia && !deferredMedia.hasAttribute('loaded')) deferredMedia.loadContent(false);
    };
  }

  function init() {
    var galleries = document.querySelectorAll(GALLERY_SELECTOR);
    if (!galleries.length) return;

    if (!observer && 'IntersectionObserver' in window) observer = createObserver();
    Array.prototype.forEach.call(galleries, startGallery);
  }

  keepAllVideosPlaying();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) return;
    Array.prototype.forEach.call(document.querySelectorAll('[' + LOADED_ATTRIBUTE + '] video'), resume);
  });

  // Variant changes and the product modal inject media after the initial render.
  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      var arrived = mutations.some(function (mutation) {
        return Array.prototype.some.call(mutation.addedNodes, function (node) {
          return (
            node.nodeType === 1 &&
            (node.nodeName === 'VIDEO' || node.nodeName === 'IFRAME' || (node.querySelector && node.querySelector('video')))
          );
        });
      });
      if (arrived) init();
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
